import { AysClinicCodeView } from '@bindoc-projects/clinic-domain-lib';

/**
 * URL builder for the SN-only clinics-api surface (`/clinics/{clinicSn}/...`).
 *
 * This is the single source of truth for the new endpoint shape — when the
 * backend finalises paths, only this file changes. Kept separate from the
 * (transitional) composite `AysClinicStore` so it can be deleted independently.
 */
export class AysClinicSnStore {
  constructor(private readonly _baseUrl: string) {}

  /**
   * Per-clinic base path. `clinicSn` is opaque and may contain hyphens
   * (`L-{ik}-{sn}`); encodeURIComponent leaves those untouched and guards
   * against any other unexpected characters.
   */
  private clinic(clinicSn: string): string {
    return `${this._baseUrl}/${encodeURIComponent(clinicSn)}`;
  }

  public search(): string {
    return `${this._baseUrl}/search`;
  }

  public byDistance(): string {
    return `${this._baseUrl}/by-distance/`;
  }

  /** Batch clinic fetch by a blueprint condition tree (`POST /clinics/by-condition`). */
  public byCondition(): string {
    return `${this._baseUrl}/by-condition`;
  }

  /**
   * Global Versorgungslevel catalog. Still served under the clinics base on the
   * SN surface (probe-verified 200 on bda-5580), so the legacy path is kept.
   */
  public availableClinicLevels(): string {
    return `${this._baseUrl}/levels/available/`;
  }

  public catalogKeys(family?: string): string {
    return family
      ? `${this._baseUrl}/catalog-keys/${family}`
      : `${this._baseUrl}/catalog-keys`;
  }

  public metadata(clinicSn: string): string {
    return `${this.clinic(clinicSn)}/metadata`;
  }

  public availableQbYears(clinicSn: string): string {
    return `${this.clinic(clinicSn)}/available-qb-years`;
  }

  public overview(clinicSn: string): string {
    return `${this.clinic(clinicSn)}/overview`;
  }

  public locations(clinicSn: string): string {
    return `${this.clinic(clinicSn)}/locations`;
  }

  public departments(clinicSn: string): string {
    return `${this.clinic(clinicSn)}/departments`;
  }

  /** Generic display section, e.g. `general`, `contacts`, `hygiene`, `staffing`. */
  public section(clinicSn: string, section: string): string {
    return `${this.clinic(clinicSn)}/${section}`;
  }

  public department(clinicSn: string, sdKey: string | number): string {
    return `${this.clinic(clinicSn)}/departments/${sdKey}`;
  }

  /**
   * One operating number's value across all of the clinic's QB years
   * (`GET /clinics/{clinicSn}/timeseries/{operatingNumber}`). The operating
   * number is a path segment (one metric per request); it must support the
   * CLINIC grain or the backend answers 400.
   */
  public timeseries(clinicSn: string, operatingNumber: string): string {
    return `${this.clinic(clinicSn)}/timeseries/${encodeURIComponent(
      operatingNumber,
    )}`;
  }

  /**
   * Per-clinic case counts for one code view
   * (`GET /clinics/{clinicSn}/codes/{codeView}`). `codeView` is usually a coding
   * system, but `OPS-115B` is a filtered view of the OPS codes — see
   * {@link AysClinicCodeView}.
   */
  public codes(clinicSn: string, codeView: AysClinicCodeView): string {
    return `${this.clinic(clinicSn)}/codes/${codeView}`;
  }

  /**
   * Per-department case counts for one code view
   * (`GET /clinics/{clinicSn}/departments/{sdKey}/codes/{codeView}`) — the
   * department-grained counterpart of {@link codes}. `sdKey` is an integer
   * specialty-department key, interpolated raw like {@link department}.
   */
  public departmentCodes(
    clinicSn: string,
    sdKey: string | number,
    codeView: AysClinicCodeView,
  ): string {
    return `${this.department(clinicSn, sdKey)}/codes/${codeView}`;
  }
}
